import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const GameLibraryPanel = ({ games, selectedGame, onGameSelect, onBulkAction }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedGames, setSelectedGames] = useState([]);
  const [viewMode, setViewMode] = useState('grid');

  const categories = [
    { value: '', label: 'All Categories' },
    { value: 'action', label: 'Action' },
    { value: 'adventure', label: 'Adventure' },
    { value: 'puzzle', label: 'Puzzle' },
    { value: 'strategy', label: 'Strategy' },
    { value: 'racing', label: 'Racing' },
    { value: 'sports', label: 'Sports' }
  ];

  const statusOptions = [
    { value: '', label: 'All Status' },
    { value: 'published', label: 'Published' },
    { value: 'draft', label: 'Draft' },
    { value: 'pending', label: 'Pending Review' },
    { value: 'rejected', label: 'Rejected' }
  ];

  const filteredGames = games.filter(game => {
    const matchesSearch = game.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         game.studio.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = !selectedCategory || game.category === selectedCategory;
    const matchesStatus = !selectedStatus || game.status === selectedStatus;
    return matchesSearch && matchesCategory && matchesStatus;
  });

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedGames(filteredGames.map(game => game.id));
    } else {
      setSelectedGames([]);
    }
  };

  const handleGameSelect = (gameId, checked) => {
    if (checked) {
      setSelectedGames([...selectedGames, gameId]);
    } else {
      setSelectedGames(selectedGames.filter(id => id !== gameId));
    }
  };

  const handleBulkAction = (action) => {
    onBulkAction(action, selectedGames);
    setSelectedGames([]);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'published': return 'text-accent';
      case 'draft': return 'text-warning';
      case 'pending': return 'text-primary';
      case 'rejected': return 'text-destructive';
      default: return 'text-muted-foreground';
    }
  };

  const getStatusBg = (status) => {
    switch (status) {
      case 'published': return 'bg-accent/10';
      case 'draft': return 'bg-warning/10';
      case 'pending': return 'bg-primary/10';
      case 'rejected': return 'bg-destructive/10';
      default: return 'bg-muted/10';
    }
  };

  return (
    <div className="h-full flex flex-col bg-card border-r border-border">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-foreground">Game Library</h2>
          <div className="flex items-center space-x-2">
            <Button
              variant={viewMode === 'grid' ? 'default' : 'ghost'}
              size="sm"
              iconName="Grid3X3"
              onClick={() => setViewMode('grid')}
            />
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              iconName="List"
              onClick={() => setViewMode('list')}
            />
          </div>
        </div>

        {/* Search */}
        <Input
          type="search"
          placeholder="Search games or studios..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="mb-4"
        />

        {/* Filters */}
        <div className="grid grid-cols-1 gap-3">
          <Select
            placeholder="Filter by category"
            options={categories}
            value={selectedCategory}
            onChange={setSelectedCategory}
          />
          <Select
            placeholder="Filter by status"
            options={statusOptions}
            value={selectedStatus}
            onChange={setSelectedStatus}
          />
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedGames.length > 0 && (
        <div className="p-4 bg-muted/30 border-b border-border">
          <div className="flex items-center justify-between">
            <span className="text-sm text-foreground">
              {selectedGames.length} game{selectedGames.length !== 1 ? 's' : ''} selected
            </span>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                iconName="Eye"
                onClick={() => handleBulkAction('publish')}
              >
                Publish
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconName="EyeOff"
                onClick={() => handleBulkAction('unpublish')}
              >
                Unpublish
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconName="Trash2"
                onClick={() => handleBulkAction('delete')}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Games List */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="space-y-2">
          {/* Select All */}
          <div className="flex items-center space-x-3 p-2 border-b border-border">
            <Checkbox
              checked={selectedGames.length === filteredGames.length && filteredGames.length > 0}
              indeterminate={selectedGames.length > 0 && selectedGames.length < filteredGames.length}
              onChange={(e) => handleSelectAll(e.target.checked)}
            />
            <span className="text-sm text-muted-foreground">
              Select All ({filteredGames.length})
            </span>
          </div>

          {/* Games */}
          {filteredGames.map((game) => (
            <div
              key={game.id}
              className={`flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                selectedGame?.id === game.id
                  ? 'bg-primary/10 border-primary/20' :'bg-background border-border hover:bg-muted/30'
              }`}
              onClick={() => onGameSelect(game)}
            >
              <Checkbox
                checked={selectedGames.includes(game.id)}
                onChange={(e) => {
                  e.stopPropagation();
                  handleGameSelect(game.id, e.target.checked);
                }}
              />
              
              <div className="w-12 h-12 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                <Image
                  src={game.thumbnail}
                  alt={game.title}
                  className="w-full h-full object-cover"
                />
              </div>
              
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-foreground truncate">{game.title}</h3>
                <p className="text-sm text-muted-foreground truncate">{game.studio}</p>
                <div className="flex items-center space-x-2 mt-1">
                  <span className={`text-xs px-2 py-1 rounded-full ${getStatusBg(game.status)} ${getStatusColor(game.status)}`}>
                    {game.status}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {game.category}
                  </span>
                </div>
              </div>
              
              <div className="flex flex-col items-end text-xs text-muted-foreground">
                <span>{new Date(game.uploadDate).toLocaleDateString()}</span>
                <span>{game.downloads.toLocaleString()} downloads</span>
              </div>
            </div>
          ))}
        </div>

        {filteredGames.length === 0 && (
          <div className="text-center py-12">
            <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">No games found</h3>
            <p className="text-muted-foreground">
              Try adjusting your search or filter criteria
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default GameLibraryPanel;